import { float, mix, mx_noise_float, sin, smoothstep, vec3 } from 'three/tsl';
import type { AtmosphereDef } from '../../engine/atmosphere/AtmosphereDef';
import type { TerrainProfile } from '../../engine/terrain/TerrainProfile';
import {
  broadNoise,
  noiseWhen,
  reliefShade,
  ridgeOf,
  type TerrainStyle,
} from '../../engine/terrain/TerrainStyle';
import type { GrassStyle, TreeStyle } from '../../engine/vegetation/VegetationStyle';
import type { HollowSceneDef } from '../scenes/HollowScene';
import { boulders } from './rocks';

type N = any;

/**
 * Umbral: a 3 km hollow with no light of its own. You bring the light: a lantern
 * lights a few metres of wet black stone and pale dead trees. Far away, crystal
 * clusters glimmer like stars, which is how you learn the shape of the world.
 */
export const UMBRAL_TERRAIN: TerrainProfile = {
  continents: { wavelength: 3000, octaves: 4, lacunarity: 2.05, gain: 0.5 },
  continentHeight: 60,
  landBias: 0.45,
  mountainMask: { wavelength: 2200, octaves: 2, lacunarity: 2.1, gain: 0.5 },
  ridges: { wavelength: 900, octaves: 7, lacunarity: 2.03, gain: 0.52 },
  ridgeHeight: 420,
  ridgeDamping: 1.0,
  hills: { wavelength: 180, octaves: 4, lacunarity: 2.05, gain: 0.5 },
  hillHeight: 14,
  detail: { wavelength: 24, octaves: 6, lacunarity: 2.07, gain: 0.5 },
  detailHeight: 1.8,
  erosion: { wavelength: 150, octaves: 4, lacunarity: 1.97, gain: 0.55, strength: 0.35 },
  snowLine: 1e9,
};

/** Still, dusty air: it only dims what little light there is. */
export const UMBRAL_AIR: AtmosphereDef = {
  rayleigh: [0.6e-6, 0.7e-6, 0.9e-6],
  rayleighHeight: 2000,
  mie: 1.0e-5,
  mieHeight: 600,
  mieAbsorption: 0.5,
  mieG: 0.5,
  ambientScatter: 0,
};

const CRYSTAL = vec3(0.35, 0.6, 1.0);

const umbralStyle: TerrainStyle = {
  // Its broad noise terms, carried by the tiles (TerrainStyle.bakedNoise).
  bakedNoise: [{ wavelength: 500, offset: [2.9, 6.3, 4.4] }, { wavelength: 400 }],
  forestMask(ctx) {
    const { p, h, slope } = ctx;
    // Crystal groves keep to the gullies.
    const n = broadNoise(ctx, p, 500, [2.9, 6.3, 4.4]).sub(ridgeOf(ctx).all.mul(0.2));
    return smoothstep(0.08, 0.35, n)
      .mul(float(1).sub(smoothstep(0.25, 0.45, slope)))
      .mul(float(1).sub(smoothstep(200, 300, h)));
  },

  land(ctx) {
    const { p, h, slope, near, mid } = ctx;
    const macro = broadNoise(ctx, p, 400);
    const fine = mx_noise_float(p.mul(1 / 40));
    const patch = mx_noise_float(p.mul(1 / 6));
    // Close by only, in a branch (multiplying by 0 saves nothing).
    const grain = noiseWhen(p.mul(1 / 1.2), near).mul(near);
    // Wet black stone with paler bands, grey silt in the hollows, bone-white patches.
    const band = sin(h.mul(0.5).add(fine.mul(4)))
      .mul(0.5)
      .add(0.5);
    const stone = mix(vec3(0.05, 0.048, 0.05), vec3(0.16, 0.15, 0.15), band.mul(0.6).add(macro.mul(0.2)));
    const silt = vec3(0.13, 0.125, 0.12);
    let land: N = mix(
      stone,
      silt,
      smoothstep(0.1, 0.5, fine.add(patch.mul(0.2))).mul(float(1).sub(slope.mul(3)).max(0)),
    );
    // The landform: silt settles down the gullies; narrow ones read as dark creases.
    const ridge = ridgeOf(ctx);
    land = mix(land, silt.mul(0.8), smoothstep(0.1, 0.65, ridge.all.negate()).mul(0.4));
    land = land
      .mul(float(1).sub(smoothstep(0.15, 0.8, ridge.narrow.negate()).mul(0.3)))
      .mul(reliefShade(ridge.relief));
    land = land.mul(grain.mul(0.35).add(1));
    const bone = vec3(0.42, 0.4, 0.36);
    land = mix(land, bone, smoothstep(0.62, 0.72, patch.add(fine.mul(0.3))).mul(0.5));
    // Crystal specks: tiny, sparse and bright, seen from far away like stars.
    const speck = smoothstep(0.78, 0.84, mx_noise_float(p.mul(1 / 3.2).add(vec3(1.1, 5.2, 2.3))))
      .mul(smoothstep(0.25, 0.6, mx_noise_float(p.mul(1 / 90))))
      .mul(mid.mul(0.5).add(0.5));
    const roughness = mix(float(0.45), float(0.8), smoothstep(0.3, 0.6, patch.add(0.5)));
    return { albedo: land, roughness, emissive: CRYSTAL.mul(speck.mul(1.6)) };
  },

  water: { shallow: [0.01, 0.012, 0.015], deep: [0.004, 0.005, 0.007], depthScale: 4 },
};

const umbralGrass: GrassStyle = {
  lush: [0.1, 0.1, 0.09],
  meadow: [0.16, 0.15, 0.13],
  tip: [0.3, 0.28, 0.24],
  roughness: 0.7,
  minHeight: 1.5,
  height: 0.45,
};

const umbralTrees: TreeStyle = {
  a: {
    shape: 'crystal',
    bark: [0.1, 0.12, 0.16],
    crown: [
      [0.2, 0.3, 0.5],
      [0.3, 0.25, 0.5],
    ],
    bendUp: 0,
    scale: 1.7,
    glow: () => CRYSTAL.mul(1.4),
    crownGlow: 0.15,
    // Crystals light the stone around them: the only light in Umbral besides yours.
    light: { height: 2, radius: 8, gain: 16 },
  },
  b: {
    shape: 'conifer',
    bark: [0.4, 0.38, 0.34],
    crown: [
      [0.3, 0.29, 0.27],
      [0.4, 0.38, 0.35],
    ],
    bendUp: 0.2,
    scale: 0.8,
  },
  aShare: [0.55, 0.8],
  loneTrees: 0.01,
  minHeight: 1.5,
};

export const UMBRAL: HollowSceneDef = {
  id: 'umbral',
  name: 'Umbral',
  center: [2.6e6, 9.4e6, -4.8e6],
  radius: 3_000,
  seed: 59,
  sun: null,
  terrain: UMBRAL_TERRAIN,
  air: UMBRAL_AIR,
  clouds: null,
  veil: {
    litFraction: 0.5,
    softness: 0.2,
    dayLength: 600,
    spinAxis: [0, 1, 0],
    duskColor: [0, 0, 0],
  },
  // The crystals are the only light: a faint, cold glow that barely outlines the land.
  glow: { color: [0.4, 0.55, 1.0], day: 0.08, night: 0.08 },
  worldAlbedo: [0.08, 0.08, 0.08],
  style: () => umbralStyle,
  grass: () => umbralGrass,
  trees: () => umbralTrees,
  rocks: () =>
    boulders(
      [
        [0.3, 0.29, 0.27],
        [0.4, 0.38, 0.35],
      ],
      [
        [0.14, 0.14, 0.15],
        [0.2, 0.2, 0.21],
      ],
      { scale: 1.2, lone: 0.02, minHeight: 1.5 },
    ),
  lantern: true,
  spawn: { dir: [0.2, -0.3, 0.93], minHeight: 3, maxHeight: 50 },
  exposureKey: 3,
  hints: ['No light at all here. L toggles your lantern.', 'The crystals show you the shape of the world.'],
  waterSkyTint: [0, 0, 0],
  ambience: {
    wind: { gain: 0.05, cutoff: 250 },
    drone: { freqs: [55, 82.4], gain: 0.018 },
    events: [{ kind: 'drip', rate: 0.3, gain: 0.05 }],
    echo: 0.5,
  },
};
